import { Component } from '@theme/component';
import { isDesktopBreakpoint, mediaQueryLarge } from '@theme/utilities';

/**
 * Sidebar navigation web component.
 * Highlights the active nav link by comparing each link's pathname
 * against the current window.location.pathname.
 * Supports mobile expand/collapse toggle via a button.
 *
 * @element sidebar-nav-component
 */
class SidebarNav extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.setActiveLink();
    window.addEventListener('popstate', this.handlePopState);
    mediaQueryLarge.addEventListener('change', this.handleDesktopChange);
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.handlePopState);

    if (isDesktopBreakpoint()) {
      mediaQueryLarge.removeEventListener('change', this.handleDesktopChange);
    }
  }

  /** @param {PopStateEvent} _event */
  handlePopState = (_event) => {
    this.setActiveLink();
  };

  /**
   * Handles viewport crossing to desktop breakpoint.
   * Closes the mobile menu when resizing to desktop.
   * @param {MediaQueryListEvent} event
   */
  handleDesktopChange = (event) => {
    if (event.matches) {
      this.closeMenu();
    }
  };

  /**
   * Toggles the mobile nav menu open/closed state.
   */
  toggleMenu() {
    const navList = this.refs['navList'];
    const toggleButton = this.refs['toggleButton'];
    const toggleIcon = this.refs['toggleIcon'];
    if (!navList) return;

    const isOpen = navList.hasAttribute('data-open');

    if (isOpen) {
      navList.removeAttribute('data-open');
    } else {
      navList.setAttribute('data-open', 'true');
    }

    if (toggleButton) {
      toggleButton.setAttribute('aria-expanded', String(!isOpen));
    }

    if (toggleIcon) {
      toggleIcon.classList.toggle('tw:rotate-180');
    }
  }

  /**
   * Closes the mobile nav menu.
   */
  closeMenu() {
    const navList = this.refs['navList'];
    const toggleButton = this.refs['toggleButton'];
    const toggleIcon = this.refs['toggleIcon'];

    if (navList) {
      navList.removeAttribute('data-open');
    }

    if (toggleButton) {
      toggleButton.setAttribute('aria-expanded', 'false');
    }

    if (toggleIcon) {
      toggleIcon.classList.remove('tw:rotate-180');
    }
  }

  /**
   * Normalizes a pathname by stripping trailing slashes.
   * @param {string} pathname
   * @returns {string}
   */
  normalizePath(pathname) {
    return pathname.replace(/\/+$/, '') || '/';
  }

  /**
   * Sets data-active="true" on the link whose href matches the current path.
   */
  setActiveLink() {
    const links = this.refs['navLinks'];
    if (!links || !Array.isArray(links)) return;

    const currentPath = this.normalizePath(window.location.pathname);

    for (const link of links) {
      if (!(link instanceof HTMLAnchorElement)) continue;

      const linkPath = this.normalizePath(link.pathname);
      const isActive = linkPath === currentPath;

      if (isActive) {
        link.setAttribute('data-active', 'true');
        link.setAttribute('aria-current', 'page');
      } else {
        link.removeAttribute('data-active');
        link.removeAttribute('aria-current');
      }
    }

    this.closeMenu();
  }
}

customElements.define('sidebar-nav-component', SidebarNav);
